import styled, { css, keyframes } from 'styled-components';
import { convertHexToRgba, getFontStyle, rem } from '../../utils';
export const StyledIcon = styled.span `
  display: inline-flex;
`;
const primaryVariant = css `
  background: ${({ theme }) => theme.Color.Brand.Primary};
  color: ${({ theme }) => theme.Color.NeutralsFixed.Paper};

  &:hover,
  &:active {
    background: ${({ theme }) => theme.Color.Brand.Dark};
  }

  &[disabled],
  &.disabled {
    background: ${({ theme }) => theme.Color.Neutrals.Light};
    color: ${({ theme }) => theme.Color.Neutrals.Gray};
  }

  ${StyledIcon} svg {
    fill: ${({ theme }) => theme.Color.NeutralsFixed.Paper};
  }
`;
const custom = css `
  ${primaryVariant} {
    background: ${({ color }) => color};
    color: ${({ theme, textColor }) => textColor !== null && textColor !== void 0 ? textColor : theme.Color.NeutralsFixed.Text};
    &:hover {
      background: ${({ color, theme, opacity }) => convertHexToRgba(color !== null && color !== void 0 ? color : theme.Color.Brand.Primary, opacity !== null && opacity !== void 0 ? opacity : 50)};
    }
  }

  ${StyledIcon} svg {
    fill: ${({ theme }) => theme.Color.NeutralsFixed.Dark};
  }
`;
const secondaryVariant = css `
  background: ${({ theme }) => theme.Color.Neutrals.Light};
  color: ${({ theme }) => theme.Color.Neutrals.Dark};

  &:hover,
  &:active {
    color: ${({ theme }) => theme.Color.Brand.Primary};

    ${StyledIcon} {
      fill: ${({ theme }) => theme.Color.Brand.Primary};
    }
  }

  &[disabled],
  &.disabled {
    background: ${({ theme }) => theme.Color.Neutrals.Light};
    color: ${({ theme }) => theme.Color.Neutrals.Gray};
  }

  ${StyledIcon} {
    fill: ${({ theme }) => theme.Color.Neutrals.Dark};
  }
`;
const ghostVariant = css `
  background: transparent;
  color: ${({ theme }) => theme.Color.Neutrals.Dark};
  &:hover,
  &:active {
    background: ${({ theme }) => theme.Components.Panel.BackgroundHover};
  }

  &[disabled],
  &.disabled {
    background: transparent;
    color: ${({ theme }) => theme.Color.Neutrals.Gray};

    ${StyledIcon} {
      fill: ${({ theme }) => theme.Color.Neutrals.Text};
    }
  }

  ${StyledIcon} {
    fill: ${({ theme }) => theme.Color.Neutrals.Dark};
  }
`;
const shadowVariant = css `
  background: ${({ theme }) => theme.Color.Neutrals.Paper};
  color: ${({ theme }) => theme.Color.Neutrals.Dark};
  box-shadow: ${({ theme }) => `0 0 ${theme.Spacing.Size.XSmall}px ${theme.Color.Overlay.Light}`};

  &:hover,
  &:active {
    color: ${({ theme }) => theme.Color.Brand.Primary};

    ${StyledIcon} {
      fill: ${({ theme }) => theme.Color.Brand.Primary};
    }
  }

  &[disabled],
  &.disabled {
    color: ${({ theme }) => theme.Color.Neutrals.Gray};
  }

  ${StyledIcon} {
    fill: ${({ theme }) => theme.Color.Neutrals.Dark};
  }
`;
const outlineVariant = css `
  background: transparent;
  color: ${({ theme }) => theme.Button.Variant.Outline.Color.Content};
  box-shadow: ${({ theme }) => `0 0 0 1px ${theme.Button.Variant.Outline.Color.Border}`}
    inset;

  &:hover,
  &:active {
    background: ${({ theme }) => theme.Button.Variant.Outline.Hover.Background};
    color: ${({ theme }) => theme.Button.Variant.Outline.Hover.Content};
    box-shadow: ${({ theme }) => `0 0 0 1px ${theme.Button.Variant.Outline.Hover.Content}`}
      inset;
    ${StyledIcon} {
      fill: ${({ theme }) => theme.Button.Variant.Outline.Hover.Content};
    }
  }

  &[disabled],
  &.disabled {
    background: ${({ theme }) => theme.Color.Neutrals.Paper};
    color: ${({ theme }) => theme.Color.Neutrals.Text};
    box-shadow: ${({ theme }) => `0 0 0 1px ${theme.Color.Neutrals.Text}`} inset;
    ${StyledIcon} {
      fill: ${({ theme }) => theme.Color.Neutrals.Text};
    }
  }

  ${StyledIcon} {
    fill: ${({ theme }) => theme.Button.Variant.Outline.Color.Content};
  }
`;
export const StyledButton = styled.button `
  white-space: nowrap;
  box-sizing: border-box;
  border-radius: ${({ theme, shape }) => shape === 'circular' ? '50%' : rem(theme.Border.Radius.Medium)};
  border: 1px solid transparent;
  cursor: pointer;
  display: inline-flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ iconPosition }) => iconPosition === 'start' ? 'row' : 'row-reverse'};
  gap: ${({ theme }) => rem(theme.Spacing.Size.XXSmall)};
  outline: none;

  ${getFontStyle('Body')}

  user-select: none;
  transition: all ${({ theme }) => theme.Transition.Timing.Out};

  &:hover {
    transition: all ${({ theme }) => theme.Transition.Timing.In};
  }

  &:active {
    transition: all ${({ theme }) => theme.Transition.Timing.In};
  }

  &[disabled],
  &.disabled {
    pointer-events: none;
  }

  ${({ variant }) => {
    switch (variant) {
        case 'secondary':
            return secondaryVariant;
        case 'ghost':
            return ghostVariant;
        case 'shadow':
            return shadowVariant;
        case 'outline':
            return outlineVariant;
        case 'custom':
            return custom;
        case 'primary':
        default:
            return primaryVariant;
    }
}}

  ${({ size }) => {
    switch (size) {
        case 'Small':
            return css `
          min-height: ${({ theme }) => rem(theme.Spacing.Size.MediumLarge)};
          padding: ${({ theme }) => rem(theme.Spacing.Size.XXSmall + 2)}; // +2px to compensate for the border
        `;
        case 'Medium':
        default:
            return css `
          min-height: ${({ theme }) => rem(theme.Spacing.Size.Large)};
          padding: ${({ theme }) => `${rem(theme.Spacing.Size.XSmall)} ${rem(theme.Spacing.Size.Small)}`};
        `;
    }
}}
`;
const LoaderAnimation = keyframes `
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;
export const StyledButtonLoader = styled.div `
  ${({ theme }) => css `
    width: ${rem(theme.Icon.Size.Medium)};
    height: ${rem(theme.Icon.Size.Medium)};
    border: ${rem(theme.Border.Size.Large)} solid ${theme.Color.Neutrals.Paper};
    border-bottom-color: ${theme.Color.Brand.Primary};
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: ${LoaderAnimation} 1s linear infinite;
  `}
`;
//# sourceMappingURL=Button.style.js.map